import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api, ApiError } from '../api.js';
import Avatar from '../components/Avatar.js';
import LanguageToggle from '../components/LanguageToggle.js';
import PinField from '../components/PinField.js';
import { GUEST_SESSION_KEY } from '../lib/useActor.js';
import logoUrl from '../assets/logo.png';
import styles from './GuestGateScreen.module.css';

/** 8MB, matching the server's cap — checked here so an oversized photo fails instantly. */
const MAX_AVATAR_BYTES = 8 * 1024 * 1024;

type Step =
  | { kind: 'roster' }
  /** Entering the PIN of an existing player. */
  | { kind: 'signIn'; memberId: string; displayName: string; avatarUrl: string | null }
  /** Claiming a player who has no PIN yet — new, or reset by the organizer. */
  | { kind: 'claim'; memberId: string; displayName: string; avatarUrl: string | null }
  | { kind: 'create' };

/**
 * Playing without an account (PLAN.md Phase 10.29).
 *
 * Reached from the invite link by choosing "play without an account" rather
 * than signing in. It shows the circle's roster and does exactly two things:
 * let someone pick the player that is already them and prove it with a PIN, or
 * add a new player and become it.
 *
 * Entries backed by a real account are shown but never selectable, and say so.
 * That is not a UI nicety — a six-digit PIN must never be an alternative door
 * into somebody's Google account, so the API refuses those entries too
 * (`guests/service.ts`'s `signInGuest`).
 *
 * Everything here happens with no session at all until the last request; the
 * invite token in the URL is what authorises the whole screen, exactly as it
 * authorises the link it came from.
 */
export default function GuestGateScreen() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>({ kind: 'roster' });

  const {
    data: roster,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['guest-roster', token],
    queryFn: () => api.getGuestRoster(token),
    retry: false,
  });

  /** Every path out of this screen ends the same way: become someone, then go to the circle. */
  const enter = async (groupId: string) => {
    await queryClient.invalidateQueries({ queryKey: GUEST_SESSION_KEY });
    void navigate(`/groups/${groupId}`, { replace: true });
  };

  if (isPending) {
    return (
      <main className={styles.screen}>
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError || !roster) {
    return (
      <main className={styles.screen}>
        <div className={`panel ${styles.card}`}>
          <h1>{t('join.invalid')}</h1>
          <p className="muted">{t('join.invalidHint')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.screen}>
      <div className={styles.topBar}>
        <LanguageToggle />
      </div>

      <div className={styles.brand}>
        <img className={styles.logo} src={logoUrl} alt="" />
      </div>

      <div className={`panel ${styles.card}`}>
        {step.kind === 'roster' ? (
          <RosterStep
            groupName={roster.groupName}
            members={roster.members}
            onPick={(member) =>
              setStep({
                kind: member.pinSet ? 'signIn' : 'claim',
                memberId: member.memberId,
                displayName: member.displayName,
                avatarUrl: member.avatarUrl,
              })
            }
            onCreate={() => setStep({ kind: 'create' })}
          />
        ) : step.kind === 'create' ? (
          <CreateStep token={token} onBack={() => setStep({ kind: 'roster' })} onDone={enter} />
        ) : (
          <PinStep
            token={token}
            step={step}
            onBack={() => setStep({ kind: 'roster' })}
            onDone={enter}
          />
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — the roster
// ---------------------------------------------------------------------------

interface RosterMember {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  hasAccount: boolean;
  pinSet: boolean;
}

function RosterStep({
  groupName,
  members,
  onPick,
  onCreate,
}: {
  groupName: string;
  members: RosterMember[];
  onPick: (member: RosterMember) => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div>
        <h1 className={styles.title}>{t('guest.pickTitle', { group: groupName })}</h1>
        <p className="muted">{t('guest.pickHint')}</p>
      </div>

      <ul className={styles.roster}>
        {members.map((member) => (
          <li key={member.memberId}>
            {member.hasAccount ? (
              // Deliberately not a button: this person signs in on the site
              // itself. The row stays visible so the list is the real roster,
              // with the reason spelled out rather than left as dead pixels.
              <div className={`${styles.member} ${styles.memberLocked}`}>
                <Avatar name={member.displayName} imageUrl={member.avatarUrl} size={40} />
                <span className={styles.memberName}>{member.displayName}</span>
                <span className={styles.memberNote}>{t('guest.hasAccount')}</span>
              </div>
            ) : (
              <button type="button" className={styles.member} onClick={() => onPick(member)}>
                <Avatar name={member.displayName} imageUrl={member.avatarUrl} size={40} />
                <span className={styles.memberName}>{member.displayName}</span>
                <span className={styles.memberBadge}>
                  {member.pinSet ? t('guest.noAccount') : t('guest.needsPin')}
                </span>
              </button>
            )}
          </li>
        ))}
        {members.length === 0 ? <p className="muted">{t('guest.rosterEmpty')}</p> : null}
      </ul>

      <button type="button" className="button button-primary" onClick={onCreate}>
        {t('guest.createNew')}
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 2a — proving a PIN, or choosing the first one
// ---------------------------------------------------------------------------

function PinStep({
  token,
  step,
  onBack,
  onDone,
}: {
  token: string;
  step: Extract<Step, { kind: 'signIn' | 'claim' }>;
  onBack: () => void;
  onDone: (groupId: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  const claiming = step.kind === 'claim';
  const mismatch = claiming && confirmPin.length === 6 && pin !== confirmPin;

  const submit = useMutation({
    mutationFn: () =>
      claiming
        ? api.setGuestPin(token, step.memberId, pin)
        : api.signInGuest(token, step.memberId, pin),
    onSuccess: (result) => void onDone(result.groupId),
  });

  const ready = pin.length === 6 && (!claiming || pin === confirmPin);

  return (
    <>
      <div className={styles.chosen}>
        <Avatar name={step.displayName} imageUrl={step.avatarUrl} size={56} />
        <h1 className={styles.title}>{step.displayName}</h1>
        <p className="muted">{claiming ? t('guest.claimHint') : t('guest.pinHint')}</p>
      </div>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready && !submit.isPending) submit.mutate();
        }}
      >
        <PinField
          id="pin"
          label={claiming ? t('guest.newPinLabel') : t('guest.pinLabel')}
          value={pin}
          onChange={setPin}
          autoFocus
        />

        {claiming ? (
          <PinField
            id="confirmPin"
            label={t('guest.confirmPinLabel')}
            value={confirmPin}
            onChange={setConfirmPin}
          />
        ) : null}

        {mismatch ? <p className="error">{t('guest.pinMismatch')}</p> : null}
        {submit.isError ? <p className="error">{errorText(t, submit.error)}</p> : null}

        <button
          type="submit"
          className="button button-primary"
          disabled={!ready || submit.isPending}
        >
          {submit.isPending ? t('guest.entering') : t('guest.enter')}
        </button>
        <button type="button" className="button button-secondary" onClick={onBack}>
          {t('common.back')}
        </button>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 2b — registering a new player
// ---------------------------------------------------------------------------

function CreateStep({
  token,
  onBack,
  onDone,
}: {
  token: string;
  onBack: () => void;
  onDone: (groupId: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mismatch = confirmPin.length === 6 && pin !== confirmPin;

  const create = useMutation({
    mutationFn: () =>
      api.createGuest(token, {
        displayName: displayName.trim(),
        pin,
        ...(avatarUrl ? { avatarUrl } : {}),
      }),
    onSuccess: (result) => void onDone(result.groupId),
  });

  const upload = async (file: File) => {
    if (file.size > MAX_AVATAR_BYTES) {
      setUploadError(t('settings.avatarTooLarge'));
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      setAvatarUrl((await api.uploadGuestAvatar(token, file)).url);
    } catch (error) {
      setUploadError(error instanceof ApiError ? error.message : t('settings.avatarUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const ready = displayName.trim().length > 0 && pin.length === 6 && pin === confirmPin;

  return (
    <>
      <div>
        <h1 className={styles.title}>{t('guest.createTitle')}</h1>
        <p className="muted">{t('guest.createHint')}</p>
      </div>

      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready && !create.isPending && !uploading) create.mutate();
        }}
      >
        <div className={styles.photoRow}>
          <button
            type="button"
            className={styles.photoButton}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            aria-label={t('guest.photoLabel')}
          >
            <Avatar name={displayName || '?'} imageUrl={avatarUrl} size={64} />
          </button>
          <div>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? t('settings.avatarUploading') : t('guest.photoLabel')}
            </button>
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              {t('guest.photoOptional')}
            </p>
            {uploadError ? <p className="error">{uploadError}</p> : null}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className={styles.fileInput}
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void upload(file);
          }}
        />

        <div>
          <label className="label" htmlFor="guestName">
            {t('guest.nameLabel')}
          </label>
          <input
            id="guestName"
            className="input"
            value={displayName}
            maxLength={60}
            placeholder={t('join.namePlaceholder')}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: 'var(--space-1)' }}>
            {t('guest.nameHint')}
          </p>
        </div>

        <PinField id="newPin" label={t('guest.newPinLabel')} value={pin} onChange={setPin} />
        <PinField
          id="newPinConfirm"
          label={t('guest.confirmPinLabel')}
          value={confirmPin}
          onChange={setConfirmPin}
        />
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          {t('guest.pinExplainer')}
        </p>

        {mismatch ? <p className="error">{t('guest.pinMismatch')}</p> : null}
        {create.isError ? <p className="error">{errorText(t, create.error)}</p> : null}

        <button
          type="submit"
          className="button button-primary"
          disabled={!ready || create.isPending || uploading}
        >
          {create.isPending ? t('guest.creating') : t('guest.create')}
        </button>
        <button type="button" className="button button-secondary" onClick={onBack}>
          {t('common.back')}
        </button>
      </form>
    </>
  );
}

/** Machine-readable API codes → the one sentence that actually helps. */
function errorText(t: (key: string) => string, error: unknown): string {
  if (!(error instanceof ApiError)) return t('common.somethingWentWrong');
  switch (error.code) {
    case 'wrong_pin':
      return t('guest.wrongPin');
    case 'too_many_attempts':
      return t('guest.tooManyAttempts');
    case 'name_taken':
      return t('join.nameTaken');
    case 'not_a_guest':
      return t('guest.hasAccountError');
    case 'pin_already_set':
      return t('guest.pinAlreadySet');
    default:
      return t('common.somethingWentWrong');
  }
}
